import { AD_SLOTS, type AdPlacement, type AdSlot } from '@accra/shared';
import { Group, Vector3, type MeshStandardMaterial } from 'three';
import { LAYOUT } from '../road/roadLayout';
import { activeRoute, tripAt, worldS } from '../route';
import { adFaceMaterial, buildBillboard, buildGantry, BILLBOARD, GANTRY } from './adModels';
import { creativeTexture, dropIdleCreatives, vacantTexture } from './adCreatives';
import { newRotation, rotate, type Rotation } from './adRotation';

const SHOW_AHEAD = 450;
const SHOW_BEHIND = 60;

interface Board {
  slot: AdSlot;
  group: Group;
  material: MeshStandardMaterial;
  /** Every ad booked on this board (empty = vacant). Shared boards take turns. */
  placements: AdPlacement[];
  rotation: Rotation;
  /** The ad on it right now. */
  showing: AdPlacement | null;
}

const SWEEP_SECONDS = 5;

export interface VisibleBoard {
  slotId: string;
  campaignId: string | null;
  centre: Vector3;
}

/** The ad boards at real places on the current route. They swap sides on the way back; shared boards rotate. */
export class AdBoards {
  readonly group = new Group();
  private boards: Board[] = [];
  private routeId = '';
  private sweep = 0;

  /** Rebuilds boards for the active route with the current bookings (none = vacant). Pictures load when needed. */
  setPlacements(placements: readonly AdPlacement[]): void {
    this.group.clear();
    this.routeId = activeRoute().id;
    this.boards = AD_SLOTS.filter((s) => s.route === this.routeId).map((slot) => {
      const material = adFaceMaterial();
      material.map = vacantTexture(slot.area);
      material.emissiveMap = material.map;
      const group = slot.format === 'gantry' ? buildGantry(material) : buildBillboard(material);
      group.visible = false;
      this.group.add(group);
      return { slot, group, material, placements: placements.filter((p) => p.slotId === slot.id), rotation: newRotation(), showing: null };
    });
  }

  /** Picks this board's ad for the frame and puts its picture on the face. */
  private choose(board: Board, inRange: boolean, dt: number): void {
    const index = rotate(board.rotation, inRange, dt, board.placements.length);
    if (!inRange) return;
    const next = board.placements[index] ?? null;
    const map = next ? creativeTexture(next.imageUrl) : vacantTexture(board.slot.area);
    if (board.material.map !== map) {
      board.material.map = map;
      board.material.emissiveMap = map;
    }
    board.showing = next;
  }

  /** Positions boards for the trip we're on. Returns the ones in range, for impression tracking. */
  update(busS: number, dt = 0): VisibleBoard[] {
    if (this.routeId !== activeRoute().id) this.setPlacements([]);
    this.sweep += dt;
    if (this.sweep > SWEEP_SECONDS) {
      this.sweep = 0;
      dropIdleCreatives();
    }
    const { trip, dir } = tripAt(busS);
    const visible: VisibleBoard[] = [];
    for (const board of this.boards) {
      const s = worldS(trip, board.slot.at);
      const inRange = s > busS - SHOW_BEHIND && s < busS + SHOW_AHEAD;
      board.group.visible = inRange;
      this.choose(board, inRange, dt);
      if (!inRange) continue;
      // Going out, 'kerb' boards are on our right; coming back they're across the median.
      const onRight = (board.slot.side === 'kerb') === (dir === 'out');
      if (board.slot.format === 'gantry') {
        board.group.position.set(0, 0, -s);
        board.group.rotation.y = 0;
        visible.push({ slotId: board.slot.id, campaignId: board.showing?.campaignId ?? null, centre: new Vector3(0, GANTRY.clearance + GANTRY.height / 2, -s) });
        continue;
      }
      const x = onRight ? LAYOUT.sidewalkX + 1.2 : LAYOUT.leftSidewalkX - 1.2;
      board.group.position.set(x, 0, -s);
      // Angled towards oncoming drivers.
      board.group.rotation.y = onRight ? 0.35 : -0.35;
      visible.push({ slotId: board.slot.id, campaignId: board.showing?.campaignId ?? null, centre: new Vector3(x, BILLBOARD.poleHeight + BILLBOARD.height / 2, -s) });
    }
    return visible;
  }
}
